import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { AuthShell } from "@/pages/Login";
import { A } from "@/lib/authStyles";

type CoachStatus = "pending" | "approved" | "rejected" | "suspended" | null;

/**
 * Wraps the coach dashboard. While the signed-in coach's row in
 * `public.coaches` is `pending` (or anything other than `approved`),
 * we render a friendly waiting-room screen instead of the dashboard.
 *
 * Auto-promotes the moment the admin flips status → 'approved' via:
 *   1. A Supabase Realtime subscription on the coach's own row.
 *   2. A 15-second polling fallback (covers networks/proxies that drop
 *      Realtime websockets).
 */
export function CoachApprovalGate({ children }: { children: React.ReactNode }) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState<CoachStatus>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const fetchStatus = async () => {
      const t0 = performance.now();
      const { data, error: err } = await supabase
        .from("coaches")
        .select("status")
        .eq("user_id", user.id)
        .maybeSingle();
      console.log(`[timing] CoachApprovalGate status query took ${(performance.now() - t0).toFixed(1)}ms`);
      if (cancelled) return;
      if (err) {
        setError(err.message);
        setLoading(false);
        return;
      }
      setStatus((data?.status as CoachStatus) ?? null);
      setLoading(false);
    };

    fetchStatus();

    // Realtime: listen for updates to this coach's row.
    const channel = supabase
      .channel(`coach-status-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "coaches",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const next = (payload.new as { status?: CoachStatus })?.status ?? null;
          setStatus(next);
        },
      )
      .subscribe();

    // Polling fallback every 15s.
    const poll = window.setInterval(fetchStatus, 15000);

    return () => {
      cancelled = true;
      window.clearInterval(poll);
      supabase.removeChannel(channel);
    };
  }, [user]);

  if (loading) {
    return (
      <AuthShell title="Loading…" subtitle="Coach AI">
        <div style={{ display: "flex", justifyContent: "center", padding: 20 }}>
          <Loader2 size={20} style={{ animation: "spin 1s linear infinite", color: A.teal }} />
        </div>
      </AuthShell>
    );
  }

  if (status === "approved") {
    return <>{children}</>;
  }

  // pending / rejected / suspended / missing → show waiting room
  const isRejected = status === "rejected";
  const isSuspended = status === "suspended";
  const isMissing = status === null;

  let title = "Application under review";
  let subtitle = "Coach AI · Pending admin approval";
  let body =
    "Thanks for applying. An admin is reviewing your coach application. We'll email you the moment you're approved — and this page will switch to your dashboard automatically.";
  let icon = <Clock size={28} color={A.teal} />;

  if (isRejected) {
    title = "Application not approved";
    body =
      "Your coach application wasn't approved at this time. If you think this is a mistake, reply to your application email and we'll take another look.";
    icon = <CheckCircle2 size={28} color={A.coral} />;
  } else if (isSuspended) {
    title = "Account suspended";
    body =
      "Your coach account has been suspended. Please contact support to restore access.";
    icon = <CheckCircle2 size={28} color={A.coral} />;
  } else if (isMissing) {
    title = "Finish your coach application";
    body =
      "We couldn't find a coach application for this account. Please complete the coach signup form to submit one.";
  }

  return (
    <AuthShell title={title} subtitle={subtitle}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 14,
          padding: "16px 18px",
          borderRadius: 14,
          border: `1px solid ${A.border}`,
          background: A.surface,
          marginBottom: 16,
        }}
      >
        <div style={{ marginTop: 2 }}>{icon}</div>
        <div style={{ fontSize: 14, color: A.text, lineHeight: 1.55 }}>{body}</div>
      </div>

      {error && (
        <div
          style={{
            background: "#f0705f18",
            border: `1px solid ${A.coral}40`,
            color: A.coral,
            padding: "10px 12px",
            borderRadius: 12,
            fontSize: 12,
            marginBottom: 14,
          }}
        >
          {error}
        </div>
      )}

      {!isRejected && !isSuspended && !isMissing && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            color: A.sub,
            fontSize: 12,
            marginBottom: 14,
          }}
        >
          <Loader2 size={14} style={{ animation: "spin 1s linear infinite" }} />
          Watching for approval…
        </div>
      )}

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {isMissing && (
          <button
            type="button"
            onClick={() => navigate("/signup-coach")}
            style={ghostButton}
          >
            Submit coach application
          </button>
        )}
        {!isRejected && !isSuspended && !isMissing && (
          <button
            type="button"
            onClick={() => navigate("/coach/profile?welcome=1")}
            style={ghostButton}
          >
            Set up profile & gallery
          </button>
        )}

        <button
          type="button"
          onClick={async () => {
            await signOut();
            navigate("/auth", { replace: true });
          }}
          style={ghostButton}
        >
          Sign out
        </button>
      </div>

      <div style={{ marginTop: 18, fontSize: 12, color: A.sub, textAlign: "center" }}>
        Need help? <Link to="/about" style={{ color: A.teal, fontWeight: 700, textDecoration: "none" }}>Contact support</Link>
      </div>
    </AuthShell>
  );
}

const ghostButton: React.CSSProperties = {
  background: "transparent",
  border: `1px solid ${A.border}`,
  color: A.text,
  padding: "10px 16px",
  borderRadius: 10,
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
};

export default CoachApprovalGate;
